import { z } from 'zod';

/**
 * Feature-flag contracts (schema-first, ADR-0008).
 *
 * These mirror what `apps/api` actually serialises — its `domain/` types, as
 * they come off `GET /api/flags` and `GET /api/flags/:key`. They are *parsed*,
 * not cast: this is a network boundary, and a response that has drifted from the
 * API should fail loudly here rather than render as `undefined` three components
 * deep.
 *
 * ## Why these are not the shapes the mock used
 *
 * The mock invented a flattened `FeatureFlagRow` (`defaultOn`, `overrides`,
 * `totalOrgs`, `owner`) because there was no API to disagree with. The real
 * response keeps the **definition** (editorial metadata an operator authors) and
 * the **default** (the row VeevartBackend's resolver actually reads) as separate
 * objects, and that separation is load-bearing rather than incidental:
 * `lifecycle: 'deprecated'` is a label, `archived: true` is the kill switch, and
 * collapsing them into one status would let a documentation edit take a feature
 * down. The UI keeps them apart for the same reason the API does.
 *
 * The mock's `totalOrgs` has no counterpart at all — see {@link FlagListItem}.
 */

export const FlagLifecycle = z.enum(['dev', 'beta', 'ga', 'deprecated']);
export type FlagLifecycle = z.infer<typeof FlagLifecycle>;

/**
 * The flag's kind (ADR 0009 §5), mirroring the API's `domain/flag-type.ts` —
 * read that file's docblock for why the two are disjoint by consumer. Chosen
 * once, at creation, and immutable afterwards. Labels and the client-side copy
 * of the Salesforce target-field rule live in `flag-type.ts`.
 */
export const FLAG_TYPES = ['veevart2', 'salesforce'] as const;
export const FlagType = z.enum(FLAG_TYPES);
export type FlagType = z.infer<typeof FlagType>;

/**
 * What a flag was born for — the axis `lifecycle` is not.
 *
 * `lifecycle` says where a flag is in its life; this says whether it has one
 * that ends. A `release` flag is engineering's, temporary, and expected to be
 * deleted at GA. An `operational` one is permanent and never sold — a kill
 * switch outlives every feature it guards. An `entitlement` one exists because
 * a museum bought it, which is why it is the only value a commercial layer can
 * be built on.
 *
 * The distinction is declared at creation rather than discovered at graduation:
 * a flag that "becomes" an entitlement either keeps its key and ties an
 * engineering switch to billing, or takes a new one and costs a non-atomic
 * change on both sides of a five-minute read cache. Neither is acceptable as
 * the normal path, and most flags are born to die.
 *
 * Mutable, unlike `type`: changing it is exactly the escape hatch for the rare
 * flag Product decides to sell months later. The audit trail is what makes that
 * safe; nothing else guards it.
 *
 * `experiment` is deliberately absent — on this axis it answers the same as
 * `release`, and `lifecycle` at `dev`/`beta` already carries it. `permission` is
 * deferred, not rejected: nobody can yet say what activating such a flag would
 * mean.
 */
export const FLAG_PURPOSES = ['release', 'operational', 'entitlement'] as const;
export const FlagPurpose = z.enum(FLAG_PURPOSES);
export type FlagPurpose = z.infer<typeof FlagPurpose>;

/**
 * What a flag with no stated purpose is taken to be.
 *
 * Honest rather than convenient: everything in the catalog today is engineering
 * work, and reading absence as `release` leaves classifying the existing flags
 * an explicit operator decision instead of a silence that looks decided.
 */
export const DEFAULT_FLAG_PURPOSE: FlagPurpose = 'release';

/**
 * The editorial row that makes a flag exist — the Backoffice's replacement for
 * VeevartBackend's code-owned `FLAG_CATALOG`. None of it is read during flag
 * resolution.
 */
export const FlagDefinition = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    category: z.string(),
    lifecycle: FlagLifecycle,
    owner: z.string(),
    /**
     * Always present on the wire: the API reads a row stored without one as
     * `veevart2` before it serialises it.
     */
    type: FlagType,
    /**
     * Absent on every row written before the attribute existed, which today is
     * all of them. `.catch` rather than `.optional().default()` for the reason
     * `lib/operator.ts:57` gives about its own enum: a value this build has
     * never heard of must degrade, not fail the row and take the list with it.
     * Both paths land on {@link DEFAULT_FLAG_PURPOSE}.
     */
    purpose: FlagPurpose.catch(DEFAULT_FLAG_PURPOSE),
    /**
     * The `V2_Salesforce_Flag__c` record a `salesforce` flag is projected to.
     * Present on every `salesforce` flag and on no `veevart2` one. Flags saved
     * before NOXSCRUM-1758 may still carry the legacy `__c` suffix.
     */
    salesforceField: z.string().optional(),
    createdAt: z.string(),
    createdBy: z.string(),
    updatedAt: z.string(),
    updatedBy: z.string(),
    /** Optimistic-concurrency token. Required on every definition write. */
    version: z.number().int(),
});
export type FlagDefinition = z.infer<typeof FlagDefinition>;

/**
 * The platform-wide default — the row the platform resolver reads.
 *
 * `rolloutPercentage` is `.optional()` rather than nullable on purpose: an
 * absent attribute means "no rollout gate", while `0` means "off for everyone".
 * A client that coerced absent to `0` would report every ungated flag as off.
 */
export const FeatureFlagDefault = z.object({
    key: z.string(),
    enabled: z.boolean(),
    rolloutPercentage: z.number().int().min(0).max(100).optional(),
    /** The real kill switch. Archived flags are never served, override or not. */
    archived: z.boolean(),
    updatedAt: z.string(),
    updatedBy: z.string(),
    version: z.number().int(),
});
export type FeatureFlagDefault = z.infer<typeof FeatureFlagDefault>;

export const FLAG_GRANT_KINDS = ['manual', 'component', 'product'] as const;
export type FlagGrantKind = (typeof FLAG_GRANT_KINDS)[number];

/**
 * One reason an override row exists — see the API's `flag-grant.ts`.
 *
 * The row works by reference counting: a product or a component adds its entry,
 * a hand-made override adds the manual one, and the row survives until the last
 * is withdrawn. That is why the UI can no longer read an override as "somebody
 * pinned this museum".
 *
 * A grant names **what** holds the row up, never who. `product` and `component`
 * carry a catalog key and there may be many of each; `manual` carries nothing,
 * because there is only one of it. The person behind a change is the audit log's
 * answer.
 */
/**
 * Bounded, and the same bound the API enforces: a museum may hold any number of
 * grants, they all live in one attribute of one row, and a row has a ceiling.
 * The charset is the platform's branded-id one, which also excludes the `#` the
 * API encodes with.
 */
export const FlagGrantId = z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,64}$/, 'A grant id must be 1-64 characters of A-Z, a-z, 0-9, _ or -');

export const FlagGrant = z.union([
    z.object({ kind: z.literal('manual') }),
    z.object({ kind: z.enum(['component', 'product']), id: FlagGrantId }),
]);
export type FlagGrant = z.infer<typeof FlagGrant>;

/** One museum's override. Sparse, and deliberately not versioned. */
export const TenantFlagOverride = z.object({
    tenantId: z.string(),
    key: z.string(),
    enabled: z.boolean(),
    rolloutPercentage: z.number().int().min(0).max(100).optional(),
    /**
     * Absent on rows written before provenance existed, and on every response
     * from an API that predates it — which is why it is optional rather than
     * defaulted to `[]`. "Nobody granted this" and "this predates the feature"
     * are different facts, and only the second one means the old behaviour is
     * still correct for the row.
     *
     * Unknown kinds are dropped rather than failing the row, matching what the
     * API's reader does with a malformed entry: provenance is display metadata,
     * and refusing the row would take an override out of the per-museum table
     * — turning a cosmetic problem into a behavioural one. A fourth grant kind
     * shipped server-side first must not blank this screen.
     */
    grantedBy: z
        .array(z.unknown())
        .transform((entries) => entries.flatMap((entry) => FlagGrant.safeParse(entry).data ?? []))
        .optional(),
});
export type TenantFlagOverride = z.infer<typeof TenantFlagOverride>;

/**
 * Why a flag resolves the way it does, computed server-side by the mirrored
 * resolution rule. The UI renders this rather than a raw `enabled`, because an
 * override of `true` on an archived flag still resolves `false` — showing the
 * stored toggle there would tell an operator the opposite of the truth.
 */
export const FlagVerdictSource = z.enum(['override', 'default', 'unset', 'archived', 'rollout']);
export type FlagVerdictSource = z.infer<typeof FlagVerdictSource>;

export const FlagVerdict = z.object({
    enabled: z.boolean(),
    source: FlagVerdictSource,
});
export type FlagVerdict = z.infer<typeof FlagVerdict>;

/* ------------------------------------------------------------------ audit - */

export const FlagAuditAction = z.enum([
    'create-flag',
    'update-definition',
    'set-default',
    // Historical only: never written since NOXSCRUM-1656, still in old history.
    'set-environment-default',
    'clear-environment-default',
    'set-override',
    'clear-override',
    // A system write with no operator behind it: the row was given the `manual`
    // grant that was already holding it up. Changes no value.
    'backfill-grants',
]);
export type FlagAuditAction = z.infer<typeof FlagAuditAction>;

export const FlagActor = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    /** The AWS account the write landed in — the account *is* the environment. */
    accountId: z.string(),
});
export type FlagActor = z.infer<typeof FlagActor>;

const FlagStateSnapshot = z.object({
    enabled: z.boolean(),
    rolloutPercentage: z.number().int().optional(),
    archived: z.boolean().optional(),
    /**
     * Who was holding the row up on this side of the change.
     *
     * The API has carried this since the grant arithmetic shipped, and this
     * schema dropped it — which meant the history could not tell *"the manual
     * grant was withdrawn and the product still grants it"* from *"nothing
     * changed"*, because both arrived as `{ enabled: true }`. Telling those apart
     * is the reason the snapshot carries grants at all.
     *
     * Tolerant like `TenantFlagOverride.grantedBy`: an unreadable entry is
     * dropped rather than blanking a screen operators read to explain an outage.
     */
    grantedBy: z
        .array(z.unknown())
        .transform((entries) => entries.flatMap((entry) => FlagGrant.safeParse(entry).data ?? []))
        .optional(),
});

const FlagDefinitionSnapshot = z.object({
    name: z.string(),
    description: z.string(),
    category: z.string(),
    lifecycle: FlagLifecycle,
    owner: z.string(),
    /**
     * On every definition snapshot, which is what puts the type on the
     * `create-flag` entry — the one place the decision is recorded as an event.
     */
    type: FlagType,
    salesforceField: z.string().optional(),
});

/**
 * Discriminated, so "renamed the flag" stays distinguishable from "turned it
 * off" in the history list.
 */
export const FlagAuditSnapshot = z.discriminatedUnion('type', [
    z.object({ type: z.literal('state'), state: FlagStateSnapshot }),
    z.object({ type: z.literal('definition'), definition: FlagDefinitionSnapshot }),
]);
export type FlagAuditSnapshot = z.infer<typeof FlagAuditSnapshot>;

export const FlagAuditEntry = z.object({
    key: z.string(),
    action: FlagAuditAction,
    tenantId: z.string().optional(),
    /**
     * The environment a historical `set-environment-default` /
     * `clear-environment-default` entry named. Nothing writes it since
     * NOXSCRUM-1656; a plain string, because it is only displayed.
     */
    environment: z.string().optional(),
    /**
     * `null` means no row existed — genuinely different from "existed and was
     * off". It is what keeps a first-time write distinguishable from a toggle
     * when someone reconstructs a timeline six months later.
     */
    before: FlagAuditSnapshot.nullable(),
    after: FlagAuditSnapshot.nullable(),
    actor: FlagActor,
    recordedAt: z.string(),
    reason: z.string().optional(),
});
export type FlagAuditEntry = z.infer<typeof FlagAuditEntry>;

/* -------------------------------------------------------------- envelopes - */

/**
 * One row of the flags list.
 *
 * There is no override count here, and that is a deliberate API decision rather
 * than an omission to paper over: counting overrides is a GSI query *per flag*,
 * so the column would cost N queries to render N rows. The detail view loads the
 * real override rows and shows a real count. The mock's "128 / 148 orgs" was a
 * number nothing in the system could have computed.
 */
export const FlagListItem = z.object({
    definition: FlagDefinition,
    default: FeatureFlagDefault,
});
export type FlagListItem = z.infer<typeof FlagListItem>;

export const FlagListResponse = z.object({ flags: z.array(FlagListItem) });

/** An overriding museum, with the verdict that override actually produces. */
export const FlagOverrideView = z.object({
    override: TenantFlagOverride,
    verdict: FlagVerdict,
});
export type FlagOverrideView = z.infer<typeof FlagOverrideView>;

export const FlagDetail = z.object({
    definition: FlagDefinition,
    default: FeatureFlagDefault,
    overrides: z.array(FlagOverrideView),
    history: z.array(FlagAuditEntry),
});
export type FlagDetail = z.infer<typeof FlagDetail>;
